import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import {
  ArrowLeft, Target, TrendingUp, DollarSign, Users, AlertTriangle,
  RefreshCw, Send, Mail, ExternalLink, CheckCircle2, Circle,
} from "lucide-react";
import { toast } from "sonner";

const CHECKLIST_KEY = "apm_launch_checklist_v1";
const DAILY_TASKS = [
  { id: "short", t: "Post 1 Short/Reel/TikTok (60-90 sec)" },
  { id: "communities", t: "Answer 3 questions in Reddit / FB groups (value-first)" },
  { id: "outbound", t: "Send 5 cold corporate outbounds" },
  { id: "drip-replies", t: "Reply to every drip-email response within 24 hrs" },
  { id: "abandoners", t: "Personal email to today's cart abandoners (below)" },
];

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function loadChecklist() {
  try {
    const raw = localStorage.getItem(CHECKLIST_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed[todayKey()] || {};
  } catch { return {}; }
}

function saveChecklist(state) {
  try {
    const raw = localStorage.getItem(CHECKLIST_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    parsed[todayKey()] = state;
    localStorage.setItem(CHECKLIST_KEY, JSON.stringify(parsed));
  } catch { /* private mode — non-fatal */ }
}

function fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.round(diffMs / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 48) return `${hrs}h ago`;
    return `${Math.round(hrs / 24)}d ago`;
  } catch { return "—"; }
}

export default function InstructorLaunch() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checks, setChecks] = useState(loadChecklist());

  const load = async () => {
    try {
      const { data: d } = await api.get("/admin/launch/dashboard");
      setData(d);
    } catch {
      toast.error("Couldn't load launch dashboard");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleCheck = (id) => {
    const next = { ...checks, [id]: !checks[id] };
    setChecks(next);
    saveChecklist(next);
  };

  const checklistDone = DAILY_TASKS.filter((t) => checks[t.id]).length;

  if (loading) return <div className="min-h-screen bg-white"><Navbar /><div className="max-w-6xl mx-auto px-6 py-20 text-zinc-500">Loading launch dashboard…</div></div>;
  if (!data) return null;

  const targetPct = data.target.paid_pct || 0;
  const mtd = data.month_to_date;
  const today = data.today;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-10" data-testid="launch-page">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-red-600 mb-4">
          <ArrowLeft className="w-4 h-4" /> Cohort dashboard
        </Link>

        <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="overline mb-2"><span className="red-bar" />30-day launch</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter">Mission control</h1>
            <p className="text-sm text-zinc-600 mt-2">
              Every metric you need to run today. Rolling 24h and 30d windows — no timezone surprises.
            </p>
          </div>
          <button onClick={load} className="btn-outline text-xs" data-testid="launch-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        {/* Target progress bar */}
        <div className="card-tech p-6 mb-6" data-testid="launch-target">
          <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Target className="w-4 h-4 text-red-600" />
                <div className="overline">100 paid students goal</div>
              </div>
              <h2 className="font-heading text-2xl font-bold tracking-tight">
                {mtd.paid_enrollments} / {data.target.paid_goal} students &middot;{" "}
                <span className="text-green-700">${Math.round(mtd.revenue).toLocaleString()}</span>{" "}
                <span className="text-sm text-zinc-500 font-normal">/ ${Math.round(data.target.revenue_goal).toLocaleString()}</span>
              </h2>
            </div>
            <div className="text-right">
              <div className="font-heading text-3xl font-bold text-red-600">{targetPct}%</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">to goal</div>
            </div>
          </div>
          <div className="h-3 bg-zinc-100 overflow-hidden border border-zinc-200" data-testid="launch-progress-bar">
            <div
              className="h-full bg-red-600 transition-all"
              style={{ width: `${targetPct}%` }}
            />
          </div>
        </div>

        {/* Today's snapshot */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <div className="card-tech p-4" data-testid="launch-today-signups">
            <Users className="w-5 h-5 text-red-600 mb-2" />
            <div className="font-heading text-3xl font-bold tracking-tight">{today.signups}</div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Signups · 24h</div>
          </div>
          <div className="card-tech p-4" data-testid="launch-today-paid">
            <TrendingUp className="w-5 h-5 text-green-600 mb-2" />
            <div className="font-heading text-3xl font-bold tracking-tight">{today.paid_enrollments}</div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Paid · 24h</div>
          </div>
          <div className="card-tech p-4" data-testid="launch-today-revenue">
            <DollarSign className="w-5 h-5 text-green-600 mb-2" />
            <div className="font-heading text-3xl font-bold tracking-tight">${Math.round(today.revenue).toLocaleString()}</div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Revenue · 24h</div>
          </div>
          <div className="card-tech p-4" data-testid="launch-today-drip">
            <Send className="w-5 h-5 text-red-600 mb-2" />
            <div className="font-heading text-3xl font-bold tracking-tight">{mtd.drip_enrolled}</div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">In drip · 30d</div>
          </div>
        </div>

        {/* Two-column: Daily checklist + Top channels */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="card-tech p-5" data-testid="launch-checklist">
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="font-heading text-lg font-bold tracking-tight">Today's playbook</h3>
              <div className="text-xs font-mono text-zinc-500">{checklistDone}/{DAILY_TASKS.length}</div>
            </div>
            <ul className="space-y-2">
              {DAILY_TASKS.map((t) => {
                const done = !!checks[t.id];
                return (
                  <li key={t.id}>
                    <button
                      onClick={() => toggleCheck(t.id)}
                      className={`w-full text-left flex items-start gap-2.5 py-1.5 group ${done ? "text-zinc-400" : "text-zinc-800"}`}
                      data-testid={`task-${t.id}`}
                    >
                      {done
                        ? <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        : <Circle className="w-5 h-5 text-zinc-300 group-hover:text-red-600 flex-shrink-0 mt-0.5" />
                      }
                      <span className={`text-sm leading-snug ${done ? "line-through" : ""}`}>{t.t}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
            <p className="text-[11px] text-zinc-500 mt-3 italic">Checklist resets at UTC midnight. Stored locally.</p>
          </div>

          <div className="card-tech p-5" data-testid="launch-channels">
            <h3 className="font-heading text-lg font-bold tracking-tight mb-3">Top channels · 30d</h3>
            {data.top_channels.length === 0 ? (
              <p className="text-sm text-zinc-500 italic">No attributed paid traffic yet. Tag your links with <code className="text-[11px] bg-zinc-100 px-1.5 py-0.5">?utm_source=…&amp;utm_campaign=…</code> to see this populate.</p>
            ) : (
              <div className="space-y-2">
                {data.top_channels.map((c, i) => (
                  <div key={`${c.utm_source}-${c.utm_campaign}-${i}`} className="flex items-center justify-between gap-3 border-l-2 border-green-600 pl-3 py-1" data-testid={`channel-${i}`}>
                    <div className="min-w-0">
                      <div className="font-mono text-xs uppercase tracking-wider text-zinc-800 truncate">{c.utm_source}</div>
                      <div className="text-[10px] text-zinc-500 truncate">{c.utm_campaign || "—"}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-heading text-base font-bold text-green-700">${Math.round(c.revenue).toLocaleString()}</div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{c.enrollments} enroll</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Cart abandoners */}
        <div className="card-tech p-5 mb-6" data-testid="launch-abandoners">
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <div className="overline">High-intent follow-ups</div>
              </div>
              <h3 className="font-heading text-lg font-bold tracking-tight">Cart abandoners · last 30d</h3>
              <p className="text-xs text-zinc-600 mt-0.5">Initiated checkout but didn't pay. Each personal email here ≈ a 15-25% close rate.</p>
            </div>
          </div>
          {data.abandoned_checkouts.length === 0 ? (
            <p className="text-sm text-zinc-500 italic mt-2">No abandoners — every checkout completed. (Or no checkouts yet.)</p>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {data.abandoned_checkouts.map((a, i) => (
                <li key={`${a.email}-${a.initiated_at}-${i}`} className="py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid={`abandoner-${i}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0" />
                      <span className="font-mono text-sm text-zinc-900 truncate">{a.email}</span>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5 truncate">
                      {a.utm_source ? `via ${a.utm_source}` : "direct"}
                      {a.utm_content ? ` · ${a.utm_content}` : ""}
                      {" · "}{fmtRelative(a.initiated_at)}
                    </div>
                  </div>
                  <a
                    href={`mailto:${a.email}?subject=Following%20up%20on%20your%20APM%20enrollment&body=Hey%2C%0A%0AI%20saw%20you%20started%20signing%20up%20for%20the%20APM%20course%20but%20didn%27t%20finish.%20Anything%20I%20can%20help%20with%3F%20Happy%20to%20answer%20questions%2C%20or%20if%20%24149%20isn%27t%20right%20right%20now%20I%27ve%20got%20a%20few%20options.%0A%0A%E2%80%94%20Trevor`}
                    className="btn-outline text-xs"
                    data-testid={`abandoner-email-${i}`}
                  >
                    <Mail className="w-3 h-3" /> Email
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Recent signups */}
        <div className="card-tech p-5 mb-6" data-testid="launch-recent">
          <h3 className="font-heading text-lg font-bold tracking-tight mb-3">Latest signups</h3>
          {data.recent_signups.length === 0 ? (
            <p className="text-sm text-zinc-500 italic">No signups yet. Time to ship some content.</p>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {data.recent_signups.map((l, i) => (
                <li key={`${l.email}-${l.created_at}-${i}`} className="py-2 flex items-center justify-between gap-3" data-testid={`recent-${i}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-sm text-zinc-900 truncate">{l.email}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                      {l.full_name || "—"} · <span className="font-mono uppercase tracking-wider">{l.source || "direct"}</span>
                    </div>
                  </div>
                  <div className="text-[11px] font-mono text-zinc-500 flex-shrink-0">{fmtRelative(l.created_at)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Quick links */}
        <div className="flex flex-wrap gap-2">
          <Link to="/instructor/playbook" className="btn-primary text-xs"><ExternalLink className="w-3 h-3" /> Daily playbook</Link>
          <Link to="/instructor/ai-drafts" className="btn-primary text-xs"><ExternalLink className="w-3 h-3" /> AI drafts</Link>
          <Link to="/instructor/corporate" className="btn-outline text-xs"><ExternalLink className="w-3 h-3" /> Corporate pipeline</Link>
          <Link to="/instructor/leads" className="btn-outline text-xs"><ExternalLink className="w-3 h-3" /> Full lead pipeline</Link>
          <Link to="/instructor/integrations" className="btn-outline text-xs"><ExternalLink className="w-3 h-3" /> n8n / Webhooks</Link>
          <Link to="/instructor/unlock" className="btn-outline text-xs"><ExternalLink className="w-3 h-3" /> Manual unlock</Link>
          <Link to="/instructor/comp-codes" className="btn-outline text-xs"><ExternalLink className="w-3 h-3" /> Comp codes</Link>
          <Link to="/instructor/email-logs" className="btn-outline text-xs"><ExternalLink className="w-3 h-3" /> Email logs</Link>
        </div>
      </main>
    </div>
  );
}
